import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  User,
  Phone,
  Mail,
  MapPin,
  PackageCheck,
  ClipboardList,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Search,
} from 'lucide-react';
import { clientesAPI, ordenesAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

function isTypingTarget(target) {
  if (!target) return false;
  const tag = (target.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  return false;
}

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/* Simple keyboard-navigable list helper */
function useListKeyboardNav(items, onSelect, listKey) {
  const [highlighted, setHighlighted] = useState(0);
  useEffect(() => { setHighlighted(0); }, [listKey]);

  const handleKey = useCallback((e) => {
    if (!items.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[highlighted];
      if (item) onSelect(item);
    }
  }, [items, highlighted, onSelect]);

  return { highlighted, setHighlighted, handleKey };
}

/* ============================================================
   Buscar cliente (Ctrl+B)
   ============================================================ */
function BuscarClienteDialog({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debouncedQuery = useDebounced(query, 250);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const handleSelect = useCallback((cli) => {
    onOpenChange(false);
    if (cli?.id) navigate(`/crm/clientes/${cli.id}`);
    else navigate('/crm/clientes');
  }, [navigate, onOpenChange]);

  const { highlighted, setHighlighted, handleKey } = useListKeyboardNav(
    results, handleSelect, debouncedQuery
  );

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const term = debouncedQuery.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    clientesAPI.listar(term)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setResults(list.slice(0, 25));
      })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden gap-0" data-testid="shortcut-cliente-dialog">
        <div className="flex items-center border-b border-border px-3">
          <Search className="w-4 h-4 text-muted-foreground mr-2 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Buscar cliente por nombre, apellido, teléfono, email o DNI…"
            className="flex h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoComplete="off"
            data-testid="shortcut-cliente-input"
          />
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando…
            </div>
          )}
          {!loading && query.trim().length < 2 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Escribe al menos 2 caracteres
            </div>
          )}
          {!loading && query.trim().length >= 2 && results.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Sin resultados para "{query}"
            </div>
          )}
          {!loading && results.length > 0 && (
            <div className="p-1">
              <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {results.length} cliente{results.length === 1 ? '' : 's'}
              </p>
              {results.map((cli, idx) => (
                <button
                  key={cli.id}
                  type="button"
                  onClick={() => handleSelect(cli)}
                  onMouseEnter={() => setHighlighted(idx)}
                  data-testid={`shortcut-cliente-item-${cli.id}`}
                  className={`w-full text-left flex items-start gap-2 px-3 py-2.5 rounded-md transition-colors ${
                    highlighted === idx ? 'bg-accent' : 'hover:bg-accent/50'
                  }`}
                >
                  <User className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">
                        {cli.nombre} {cli.apellidos || ''}
                      </span>
                      {cli.dni && <span className="text-xs font-mono text-muted-foreground">{cli.dni}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      {cli.telefono && (
                        <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{cli.telefono}</span>
                      )}
                      {cli.email && (
                        <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{cli.email}</span>
                      )}
                      {cli.ciudad && (
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{cli.ciudad}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-border px-3 py-2 text-[10px] text-muted-foreground flex items-center justify-between">
          <span>Atajo: <kbd className="px-1.5 py-0.5 bg-muted rounded font-mono text-[10px]">Ctrl+B</kbd></span>
          <span><kbd className="px-1 py-0.5 bg-muted rounded">↵</kbd> abrir · <kbd className="px-1 py-0.5 bg-muted rounded">↑↓</kbd> navegar · <kbd className="px-1 py-0.5 bg-muted rounded">Esc</kbd> cerrar</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   Registrar recibido (Ctrl+R)
   ============================================================ */
function RegistrarRecibidoDialog({ open, onOpenChange }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [last, setLast] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCode('');
      setLast(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleRegistrar = useCallback(async (codigo) => {
    const term = (codigo || '').trim();
    if (!term) return;
    setLoading(true);
    setLast(null);
    try {
      const res = await ordenesAPI.buscarPorCodigo(term);
      const data = res.data || {};
      const ordenes = Array.isArray(data.ordenes) ? data.ordenes : [];

      if (data.accion === 'recibida' && ordenes.length >= 1) {
        const orden = ordenes[0];
        setLast({
          ok: true,
          message: `Orden ${orden.numero_orden || term} registrada como RECIBIDA`,
          orden,
        });
        toast.success(`Recibida: ${orden.numero_orden || term}`);
        window.dispatchEvent(new Event('notificaciones-updated'));
      } else if (ordenes.length === 1) {
        const orden = ordenes[0];
        setLast({
          ok: false,
          message: `La orden ${orden.numero_orden} ya está en estado "${orden.estado}". No se puede marcar como recibida.`,
          orden,
        });
        toast.warning(`Estado actual: ${orden.estado}`);
      } else if (ordenes.length > 1) {
        setLast({
          ok: false,
          message: `Encontradas ${ordenes.length} órdenes. Refina la búsqueda.`,
        });
      } else {
        setLast({ ok: false, message: data.mensaje || 'No se encontró ninguna orden con ese código' });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Error en la búsqueda';
      setLast({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
      setCode('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="shortcut-recibir-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-primary" />
            Registrar Recibido
          </DialogTitle>
          <DialogDescription>
            Escanea o pega el código de barras / número de orden / autorización. La orden se marca como{' '}
            <span className="font-medium text-foreground">RECIBIDA</span> automáticamente sin abrirla.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => { e.preventDefault(); handleRegistrar(code); }}
          className="space-y-3"
        >
          <Input
            ref={inputRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Escanea código…"
            className="font-mono text-base h-11"
            autoComplete="off"
            disabled={loading}
            data-testid="shortcut-recibir-input"
          />
          <div className="flex gap-2">
            <Button type="submit" disabled={loading || !code.trim()} className="flex-1 gap-1.5" data-testid="shortcut-recibir-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {loading ? 'Registrando…' : 'Registrar como recibida'}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          </div>
        </form>

        {last && (
          <div
            className={`mt-2 p-3 rounded-lg border text-sm ${
              last.ok
                ? 'bg-green-500/10 border-green-500/30 text-green-400'
                : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
            }`}
            data-testid="shortcut-recibir-result"
          >
            <div className="flex items-start gap-2">
              {last.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
              <div className="flex-1">
                <p>{last.message}</p>
                {last.orden && (
                  <p className="text-xs mt-1 text-muted-foreground">
                    {last.orden.dispositivo?.modelo || ''}
                    {last.orden.cliente?.nombre ? ` · ${last.orden.cliente.nombre}` : ''}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground text-center">
          Atajo: <kbd className="px-1 py-0.5 bg-muted rounded font-mono">Ctrl+R</kbd> · Listo para escaneo continuo
        </p>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   Buscar y abrir orden (Ctrl+A)
   ============================================================ */
function BuscarOrdenDialog({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debouncedQuery = useDebounced(query, 300);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const handleSelect = useCallback((orden) => {
    if (!orden?.id) return;
    onOpenChange(false);
    navigate(`/crm/ordenes/${orden.id}`);
  }, [navigate, onOpenChange]);

  const { highlighted, setHighlighted, handleKey } = useListKeyboardNav(
    results, handleSelect, debouncedQuery
  );

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const term = debouncedQuery.trim();
    if (term.length < 2) {
      setResults([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    ordenesAPI.buscarPorCodigo(term)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        const ordenes = Array.isArray(data.ordenes) ? data.ordenes : [];
        setResults(ordenes.slice(0, 30));
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Error en la búsqueda');
          setResults([]);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden gap-0" data-testid="shortcut-orden-dialog">
        <div className="flex items-center border-b border-border px-3">
          <Search className="w-4 h-4 text-muted-foreground mr-2 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Buscar orden por número, autorización, código GLS o IMEI…"
            className="flex h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoComplete="off"
            data-testid="shortcut-orden-input"
          />
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando…
            </div>
          )}
          {!loading && query.trim().length < 2 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Escribe al menos 2 caracteres
            </div>
          )}
          {!loading && error && (
            <div className="py-8 text-center text-sm text-destructive">{error}</div>
          )}
          {!loading && !error && query.trim().length >= 2 && results.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Sin resultados para "{query}"
            </div>
          )}
          {!loading && results.length > 0 && (
            <div className="p-1">
              <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {results.length === 1 ? '1 orden encontrada — pulsa Enter para abrir' : `${results.length} órdenes — elige una`}
              </p>
              {results.map((orden, idx) => (
                <button
                  key={orden.id}
                  type="button"
                  onClick={() => handleSelect(orden)}
                  onMouseEnter={() => setHighlighted(idx)}
                  data-testid={`shortcut-orden-item-${orden.id}`}
                  className={`w-full text-left flex items-start gap-2 px-3 py-2.5 rounded-md transition-colors ${
                    highlighted === idx ? 'bg-accent' : 'hover:bg-accent/50'
                  }`}
                >
                  <ClipboardList className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {orden.numero_autorizacion ? (
                        <span className="font-mono font-bold text-primary">{orden.numero_autorizacion}</span>
                      ) : (
                        <span className="font-mono font-medium">{orden.numero_orden}</span>
                      )}
                      {orden.estado && (
                        <Badge className={`badge-status status-${orden.estado} text-[10px]`}>
                          {orden.estado.replace('_', ' ')}
                        </Badge>
                      )}
                      {orden.numero_autorizacion && (
                        <span className="text-[10px] font-mono text-muted-foreground">{orden.numero_orden}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {orden.dispositivo?.modelo || 'Sin modelo'}
                      {orden.cliente?.nombre && ` · ${orden.cliente.nombre}`}
                      {orden.dispositivo?.imei && ` · IMEI ${String(orden.dispositivo.imei).slice(-6)}`}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-border px-3 py-2 text-[10px] text-muted-foreground flex items-center justify-between">
          <span>Atajo: <kbd className="px-1.5 py-0.5 bg-muted rounded font-mono text-[10px]">Ctrl+A</kbd></span>
          <span><kbd className="px-1 py-0.5 bg-muted rounded">↵</kbd> abrir · <kbd className="px-1 py-0.5 bg-muted rounded">↑↓</kbd> navegar · <kbd className="px-1 py-0.5 bg-muted rounded">Esc</kbd> cerrar</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   Componente principal
   ============================================================ */
export function KeyboardShortcuts() {
  const { user } = useAuth();
  const [openCliente, setOpenCliente] = useState(false);
  const [openRecibir, setOpenRecibir] = useState(false);
  const [openOrden, setOpenOrden] = useState(false);

  useEffect(() => {
    if (!user) return undefined;

    const handler = (e) => {
      const meta = e.ctrlKey || e.metaKey;
      if (!meta || e.shiftKey || e.altKey) return;

      const key = (e.key || '').toLowerCase();
      if (!['b', 'r', 'a'].includes(key)) return;

      const typing = isTypingTarget(e.target);
      // Ctrl+R y Ctrl+A son atajos globales (Ctrl+R sustituye recargar, Ctrl+A select-all). Ctrl+B respeta el foco en inputs.
      if (typing && key === 'b') return;

      e.preventDefault();
      e.stopPropagation();

      if (key === 'b') {
        setOpenRecibir(false);
        setOpenOrden(false);
        setOpenCliente(true);
      } else if (key === 'r') {
        setOpenCliente(false);
        setOpenOrden(false);
        setOpenRecibir(true);
      } else if (key === 'a') {
        setOpenCliente(false);
        setOpenRecibir(false);
        setOpenOrden(true);
      }
    };

    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [user]);

  if (!user) return null;

  return (
    <>
      <BuscarClienteDialog open={openCliente} onOpenChange={setOpenCliente} />
      <RegistrarRecibidoDialog open={openRecibir} onOpenChange={setOpenRecibir} />
      <BuscarOrdenDialog open={openOrden} onOpenChange={setOpenOrden} />
    </>
  );
}
